/**
 * keyWeights.js — which survey weight column each response key is averaged over.
 *
 * Background
 * ----------
 * Every percentage the app shows is a weighted mean. The backend computes the
 * per-area figure as
 *
 *     100 * SUM(w * value) / SUM(w)
 *
 * and the frontend rolls those per-area figures up to a constituency / national
 * figure the same way. `w` is a survey weight column, and it is NOT the same
 * column for every question:
 *
 *   n_raked   — raked weight over all adults. The default: use it for anything
 *               asked of (and answered by) the whole sample.
 *   n_voters  — raked weight over likely voters. Vote intention and best-PM are
 *               vote-share questions; averaging them over all adults gives a
 *               share of the wrong population.
 *
 * Single source of truth
 * ----------------------
 * The column is declared ONCE, on the group in polAggregators (`weightColumn`),
 * and everything else derives from it — this module for every frontend average,
 * and core/key_weights.py for the SQL denominator. A question that needs a
 * non-default base only ever needs `weightColumn` added to its group.
 *
 * The map is NOT sent on the request: the backend reads the same aggregator
 * declaration (via core/extract_metrics.mjs) rather than trusting a client to
 * name a SQL denominator. core/tests.py::KeyWeightSyncTests asserts the two
 * sides agree, so they cannot drift apart silently.
 */
import { getPolConfig } from "./polAggregators";

/** Weight column used when a group does not declare one. */
export const DEFAULT_WEIGHT_COL = "n_raked";

/**
 * Weight columns that actually exist in the parquet mirror. A group declaring
 * anything else is ignored (and falls back to the default) rather than silently
 * producing NULL denominators.
 *
 * Mirrors ALLOWED_WEIGHT_COLS in core/key_weights.py.
 */
export const ALLOWED_WEIGHT_COLS = new Set(["n_raked", "n_voters"]);

/**
 * Row-level fallbacks, in order, for reading a weight off an aggregated row.
 * The backend always returns n_raked; the others are legacy column names still
 * produced by a few older endpoints.
 */
const WEIGHT_FALLBACKS = ["n_raked", "total_sum", "total", "weight_vec", "weight", "n"];

/** The weight column a group's keys are averaged over. */
export function weightColForGroup(group) {
  const declared = group?.weightColumn;
  return ALLOWED_WEIGHT_COLS.has(declared) ? declared : DEFAULT_WEIGHT_COL;
}

/**
 * Build a { key → weight column } map from a groupsById object. Only keys whose
 * column differs from the default are included, so the map stays small enough
 * to post on a request body.
 */
export function buildKeyWeightMap(groupsById) {
  const out = {};
  for (const group of Object.values(groupsById || {})) {
    const col = weightColForGroup(group);
    if (col === DEFAULT_WEIGHT_COL) continue;
    for (const k of group?.keys || []) out[k] = col;
  }
  return out;
}

let _defaultMap = null;

/** The key → weight column map for the built-in (polAggregators) groups. */
export function defaultKeyWeightMap() {
  if (_defaultMap === null) {
    try {
      _defaultMap = buildKeyWeightMap(getPolConfig().groupsById);
    } catch {
      // A malformed aggregator must not take the whole app down — every key
      // then falls back to n_raked, which is what the old code did anyway.
      _defaultMap = {};
    }
  }
  return _defaultMap;
}

/**
 * The weight column for one response key.
 *
 * @param {string} key
 * @param {object} [keyWeightMap] - overrides the built-in map (client datasets)
 */
export function weightColForKey(key, keyWeightMap) {
  const map = keyWeightMap || defaultKeyWeightMap();
  const col = map?.[key];
  return ALLOWED_WEIGHT_COLS.has(col) ? col : DEFAULT_WEIGHT_COL;
}

/**
 * Read the weight for `key` off an aggregated row.
 *
 * Falls back to n_raked (then the legacy column names) when the row does not
 * carry the requested column — an older cached response, or a schema whose
 * parquet has no n_voters column.
 *
 * @returns {number|null} a positive weight, or null when the row has none
 */
export function weightForRow(row, key, keyWeightMap) {
  return weightFromRowByCol(row, weightColForKey(key, keyWeightMap));
}

/**
 * Read a specific weight column off a row, with the same fallback chain.
 *
 * @returns {number|null} a positive weight, or null when the row has none
 */
export function weightFromRowByCol(row, weightCol) {
  if (!row) return null;
  for (const col of [weightCol || DEFAULT_WEIGHT_COL, ...WEIGHT_FALLBACKS]) {
    const v = Number(row[col]);
    if (Number.isFinite(v) && v > 0) return v;
  }
  return null;
}

/**
 * Read a cell as a number, treating an absent answer as absent.
 *
 * NOT Number(): a question that was not asked in a wave comes back as NULL,
 * and Number(null) is 0 — a finite value that would be averaged in as a
 * genuine zero and drag the figure down.
 *
 * @returns {number|null}
 */
export function numericOrNull(value) {
  if (value === null || value === undefined || value === "") return null;
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
}

/**
 * Weighted mean of `keys` across `rows`, each key using its own weight column.
 *
 * Denominators accumulate PER KEY and skip rows where the value is missing: a
 * question that was not asked in a given wave comes back NULL, and counting its
 * rows in the denominator would drag the average toward zero.
 *
 * @param {Array<object>} rows
 * @param {Array<string>} keys
 * @param {object}   [opts]
 * @param {function} [opts.valueFn]      - (row, key) => number|null
 * @param {object}   [opts.keyWeightMap]
 * @returns {Object<string, number|null>}
 */
export function weightedMeanByKey(rows, keys, opts = {}) {
  const { valueFn, keyWeightMap } = opts;
  const readValue = valueFn || ((row, key) => numericOrNull(row?.[key]));

  const num = {};
  const den = {};
  for (const row of rows || []) {
    for (const key of keys || []) {
      const v = readValue(row, key);
      if (!Number.isFinite(v)) continue;
      // No usable weight on the row → count it evenly rather than dropping it,
      // so a response missing its weight column still contributes.
      const w = weightForRow(row, key, keyWeightMap) ?? 1;
      num[key] = (num[key] || 0) + w * v;
      den[key] = (den[key] || 0) + w;
    }
  }

  const out = {};
  for (const key of keys || []) out[key] = den[key] > 0 ? num[key] / den[key] : null;
  return out;
}

/**
 * Weighted mean of a single derived value (e.g. a likert net) across rows, all
 * sharing one weight column.
 *
 * @param {Array<object>} rows
 * @param {function} valueFn   - (row) => number|null
 * @param {string}   weightCol - from weightColForGroup()
 * @returns {number|null}
 */
export function weightedMean(rows, valueFn, weightCol = DEFAULT_WEIGHT_COL) {
  let num = 0;
  let den = 0;
  for (const row of rows || []) {
    const v = valueFn(row);
    if (!Number.isFinite(v)) continue;
    const w = weightFromRowByCol(row, weightCol) ?? 1;
    num += w * v;
    den += w;
  }
  return den > 0 ? num / den : null;
}
